import { useState } from 'react';
import { documentService } from '../services';

/**
 * Hook for managing document uploads and OCR processing
 */
export const useDocumentUpload = (hotelId: string | undefined, onSuccess?: (message: string) => void) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const uploadDocument = async (file: File, documentType: string = 'Miscellaneous Document') => {
    if (!hotelId) {
      setError('Hotel ID is required');
      return false;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`);
      return false;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await documentService.uploadDocument(file, hotelId, documentType);

      const message = result.status === 409
        ? `Document processed as "${documentType}". Some duplicate entries were updated with new values.`
        : `Document "${file.name}" successfully processed as "${documentType}". Data is now available in the OCR Results tab.`;

      setSuccess(message);
      if (onSuccess) {
        onSuccess(message);
      }
      return true;
    } catch (error: any) {
      console.error('OCR Processing Error:', error);
      let errorMessage = 'Failed to process document';

      if (error.response?.status === 409) {
        const message = `Document "${file.name}" processed as "${documentType}". Existing entries have been updated with new values.`;
        setSuccess(message);
        if (onSuccess) {
          onSuccess(message);
        }
        return true;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid request: Please check file format and try again';
      } else if (error.response?.status === 413) {
        errorMessage = 'File size too large';
      } else if (error.message.includes('size exceeds')) {
        errorMessage = error.message;
      }

      setError(`${errorMessage} - ${error.response?.data?.message || error.message}`);
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    error,
    success,
    uploadDocument,
    setError,
    setSuccess
  };
};